import { useState, useEffect, useCallback, ChangeEvent, FormEvent } from 'react';
import validate from '../components/validation/validate';
import emailjs from 'emailjs-com';
import { logger } from '../utils/logger';

export interface AccessibilityFormValues {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  message: string;
}

export interface BlurStatus {
  email: boolean;
  firstName: boolean;
  lastName: boolean;
  phoneNumber: boolean;
  message: boolean;
}

export type FormErrors = Record<string, string>;

export interface UseAccessibilityFormReturn {
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  values: AccessibilityFormValues;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  errors: FormErrors;
  onBlurStatus: BlurStatus;
  handleBlur: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  isSubmitting: boolean;
  isSubmitted: boolean;
  submitError: Error | null;
}

const useAccessibilityForm = (): UseAccessibilityFormReturn => {
  const [values, setValues] = useState<AccessibilityFormValues>({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    message: '',
  });

  const [onBlurStatus, setOnBlurStatus] = useState<BlurStatus>({
    email: false,
    firstName: false,
    lastName: false,
    phoneNumber: false,
    message: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<Error | null>(null);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleBlur = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name } = e.target;
    setOnBlurStatus((prev) => ({ ...prev, [name]: true }));
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      setSubmitError(null);

      // Validate and get fresh errors (not stale state)
      const validationErrors = validate(values, onBlurStatus);
      setErrors(validationErrors);

      // Check the fresh validation result, not stale state
      if (Object.keys(validationErrors).length === 0) {
        setIsSubmitting(true);

        emailjs
          .sendForm(
            process.env.REACT_APP_EMAILJS_SERVICE_ID!,
            process.env.REACT_APP_EMAILJS_ACCESSIBILITY_TEMPLATE_ID!,
            '#contactForm',
            process.env.REACT_APP_EMAILJS_USER_ID!
          )
          .then(() => {
            setIsSubmitting(false);
            setIsSubmitted(true);
          })
          .catch((err: Error) => {
            logger.error('Accessibility form submission failed:', err);
            setIsSubmitting(false);
            setSubmitError(err);
          });
      }
    },
    [values, onBlurStatus]
  );

  // Validate on input changes for real-time feedback
  useEffect(() => {
    setErrors(validate(values, onBlurStatus));
  }, [values, onBlurStatus]);

  return {
    handleChange,
    values,
    handleSubmit,
    errors,
    onBlurStatus,
    handleBlur,
    isSubmitting,
    isSubmitted,
    submitError,
  };
};

export default useAccessibilityForm;
